import React, { useState, useEffect } from 'react';
import { dealerCommissionSettingsApi } from '@/services/api';
import { CommissionSettings, UpdateCommissionSettingsDto } from '@/types';

export function CommissionSettingsPage() {
  const [settings, setSettings] = useState<CommissionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [salesManagerDefaultRate, setSalesManagerDefaultRate] = useState<number>(5.0);
  const [salesRepDefaultRate, setSalesRepDefaultRate] = useState<number>(8.0);
  const [commissionLockPoint, setCommissionLockPoint] = useState<'submitted' | 'pending_release' | 'shipped' | 'delivered'>('confirmed');
  const [includeCustomItems, setIncludeCustomItems] = useState(true);
  const [includeInstallation, setIncludeInstallation] = useState(true);
  const [includeShipping, setIncludeShipping] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dealerCommissionSettingsApi.get();
      setSettings(data);

      // Populate form (handle both camelCase and snake_case from API)
      setTrackingEnabled(data.commission_tracking_enabled ?? true);
      setSalesManagerDefaultRate(data.salesManagerDefaultRate ?? data.sales_manager_default_rate ?? 5.0);
      setSalesRepDefaultRate(data.salesRepDefaultRate ?? data.sales_rep_default_rate ?? 8.0);
      setCommissionLockPoint((data.commissionLockPoint ?? data.commission_lock_point ?? 'pending_release') as any);
      setIncludeCustomItems(data.commission_include_custom_items ?? true);
      setIncludeInstallation(data.commission_include_installation ?? true);
      setIncludeShipping(data.commission_include_shipping ?? false);
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updates: UpdateCommissionSettingsDto = {
        commissionTrackingEnabled: trackingEnabled,
        salesManagerDefaultRate,
        salesRepDefaultRate,
        commissionLockPoint,
        commissionIncludeScreens: true,
        commissionIncludeCustomItems: includeCustomItems,
        commissionIncludeInstallation: includeInstallation,
        commissionIncludeShipping: includeShipping,
      };

      const updatedSettings = await dealerCommissionSettingsApi.update(updates);
      setSettings(updatedSettings);
      setSuccessMessage('Commission settings saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setTrackingEnabled(settings.commission_tracking_enabled ?? true);
    setSalesManagerDefaultRate(settings.salesManagerDefaultRate ?? settings.sales_manager_default_rate ?? 5.0);
    setSalesRepDefaultRate(settings.salesRepDefaultRate ?? settings.sales_rep_default_rate ?? 8.0);
    setCommissionLockPoint((settings.commissionLockPoint ?? settings.commission_lock_point ?? 'pending_release') as any);
    setIncludeCustomItems(settings.commission_include_custom_items ?? true);
    setIncludeInstallation(settings.commission_include_installation ?? true);
    setIncludeShipping(settings.commission_include_shipping ?? false);
  };

  const settingsManagerRate = settings?.salesManagerDefaultRate ?? settings?.sales_manager_default_rate ?? 5.0;
  const settingsRepRate = settings?.salesRepDefaultRate ?? settings?.sales_rep_default_rate ?? 8.0;
  const settingsLockPoint = settings?.commissionLockPoint ?? settings?.commission_lock_point ?? 'pending_release';

  const hasChanges = settings && (
    trackingEnabled !== (settings.commission_tracking_enabled ?? true) ||
    salesManagerDefaultRate !== settingsManagerRate ||
    salesRepDefaultRate !== settingsRepRate ||
    commissionLockPoint !== settingsLockPoint ||
    includeCustomItems !== (settings.commission_include_custom_items ?? true) ||
    includeInstallation !== (settings.commission_include_installation ?? true) ||
    includeShipping !== (settings.commission_include_shipping ?? false)
  );

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Disclaimer Banner */}
      <div className="text-xs text-gray-500 bg-keder-dark/50 px-4 py-2 rounded border border-keder-gray/30">
        Commission tracking is a convenience tool. Keder Screens does not pay or verify commissions.
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Commission Settings</h1>
        <p className="text-gray-400 mt-1">Configure default commission rates and earning rules</p>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="card bg-green-900/20 border-green-800">
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (
        <>
          {/* Disclaimer Card */}
          <div className="card bg-blue-900/10 border-blue-800">
            <div className="flex items-start gap-3">
              <svg className="h-6 w-6 text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-white font-semibold mb-2">Important Information</h3>
                <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                  <li>Commission tracking is for <strong>your internal use only</strong></li>
                  <li>Keder is <strong>not involved</strong> in your payroll or commission payments</li>
                  <li>Commissions are calculated on <strong>Customer Sell Price</strong> (your markup to end customer), not your dealer cost</li>
                  <li>Default rates apply to new team members unless a custom rate is set</li>
                  <li>Lock point determines when commissions move from "pending" to "earned"</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Settings Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Master Toggle — controls whether dealer portal shows
                "Record Your Sales" prompts, Customer Sell Price inputs,
                and Your Sale/Profit widgets. Off = silent. */}
            <div className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-white">Internal Commission Tracking</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    When enabled, the portal will prompt you to record customer sell prices on
                    delivered orders and show "Your Sale / Your Profit" calculations. Turn it off if
                    you don't run an internal commission program — Keder-side commissions (rep firms,
                    sales rep attribution) are not affected either way.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={trackingEnabled}
                    onChange={(e) => setTrackingEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-keder-yellow"></div>
                </label>
              </div>
            </div>

            {/* Default Rates Card */}
            <div className={trackingEnabled ? 'card' : 'card opacity-50 pointer-events-none'}>
              <h2 className="text-lg font-semibold text-white mb-4">Default Commission Rates</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Sales Manager Default Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={salesManagerDefaultRate}
                    onChange={(e) => setSalesManagerDefaultRate(parseFloat(e.target.value) || 0)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-field"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Applied to new sales managers unless overridden
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Sales Rep Default Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={salesRepDefaultRate}
                    onChange={(e) => setSalesRepDefaultRate(parseFloat(e.target.value) || 0)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-field"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Applied to new sales reps unless overridden
                  </p>
                </div>
              </div>
            </div>

            {/* Commissionable Amount Card */}
            <div className={trackingEnabled ? 'card' : 'card opacity-50 pointer-events-none'}>
              <h2 className="text-lg font-semibold text-white mb-2">Commissionable Amount</h2>
              <p className="text-sm text-gray-400 mb-4">Choose what's included when calculating the commission base from Price Builder estimates.</p>
              <div className="space-y-3">
                {/* Screens — always on */}
                <label className="flex items-center justify-between p-3 bg-keder-gray/30 rounded-lg opacity-70">
                  <div>
                    <span className="text-white font-medium">Keder Screens</span>
                    <p className="text-xs text-gray-500">Screen products + packaging</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Always included</span>
                    <div className="w-10 h-6 bg-keder-yellow rounded-full relative cursor-not-allowed">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </label>

                {/* Custom Items */}
                <label className="flex items-center justify-between p-3 bg-keder-gray/30 rounded-lg cursor-pointer hover:bg-keder-gray/40 transition-colors">
                  <div>
                    <span className="text-white font-medium">Custom Items</span>
                    <p className="text-xs text-gray-500">Third-party products added in Price Builder</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIncludeCustomItems(!includeCustomItems)}
                    className={`w-10 h-6 rounded-full relative transition-colors ${includeCustomItems ? 'bg-keder-yellow' : 'bg-keder-gray'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${includeCustomItems ? 'right-1' : 'left-1'}`} />
                  </button>
                </label>

                {/* Installation */}
                <label className="flex items-center justify-between p-3 bg-keder-gray/30 rounded-lg cursor-pointer hover:bg-keder-gray/40 transition-colors">
                  <div>
                    <span className="text-white font-medium">Installation</span>
                    <p className="text-xs text-gray-500">Installation fees from Price Builder</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIncludeInstallation(!includeInstallation)}
                    className={`w-10 h-6 rounded-full relative transition-colors ${includeInstallation ? 'bg-keder-yellow' : 'bg-keder-gray'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${includeInstallation ? 'right-1' : 'left-1'}`} />
                  </button>
                </label>

                {/* Shipping */}
                <label className="flex items-center justify-between p-3 bg-keder-gray/30 rounded-lg cursor-pointer hover:bg-keder-gray/40 transition-colors">
                  <div>
                    <span className="text-white font-medium">Shipping</span>
                    <p className="text-xs text-gray-500">Shipping costs (typically pass-through)</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIncludeShipping(!includeShipping)}
                    className={`w-10 h-6 rounded-full relative transition-colors ${includeShipping ? 'bg-keder-yellow' : 'bg-keder-gray'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${includeShipping ? 'right-1' : 'left-1'}`} />
                  </button>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Discounts are applied before commission calculation. Tax is never included in the commissionable amount.
              </p>
            </div>

            {/* Commission Lock Point Card */}
            <div className={trackingEnabled ? 'card' : 'card opacity-50 pointer-events-none'}>
              <h2 className="text-lg font-semibold text-white mb-4">Commission Earning Rule</h2>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Commission Lock Point
                </label>
                <select
                  value={commissionLockPoint}
                  onChange={(e) => setCommissionLockPoint(e.target.value as any)}
                  className="select-field"
                  required
                >
                  <option value="submitted">Submitted - Commission earned when order is submitted</option>
                  <option value="pending_release">Pending Release - Commission earned when order is approved</option>
                  <option value="shipped">Shipped - Commission earned when order ships</option>
                  <option value="delivered">Delivered - Commission earned when order is delivered</option>
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  When an order reaches this status, commissions change from "pending" to "earned"
                </p>
              </div>

              {/* Lock Point Explanation */}
              <div className="mt-4 p-4 bg-keder-gray/30 rounded-lg">
                <h4 className="text-sm font-medium text-white mb-2">How Lock Point Works:</h4>
                <div className="text-sm text-gray-300 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-500 font-bold">1.</span>
                    <span><strong>Order Created:</strong> Commission record created with status "pending"</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-500 font-bold">2.</span>
                    <span><strong>Lock Point Reached:</strong> Commission automatically changes to "earned"</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-yellow-500 font-bold">3.</span>
                    <span><strong>Manual Payment:</strong> Admin marks commission as "paid" with payment reference</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Basis Explanation */}
            <div className="card bg-keder-gray/20">
              <h2 className="text-lg font-semibold text-white mb-4">Commission Calculation</h2>
              <div className="space-y-3 text-sm text-gray-300">
                <p>
                  Commissions are calculated based on the <strong className="text-white">Customer Sell Price</strong> field when creating an order.
                </p>
                <div className="p-4 bg-keder-dark rounded-lg border border-keder-gray">
                  <p className="text-keder-yellow font-mono mb-2">
                    Commission Amount = Customer Sell Price × Commission Rate %
                  </p>
                  <p className="text-xs text-gray-400">
                    Example: $10,000 customer sale × 8% = $800 commission
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  Note: The "Customer Sell Price" is the price YOU charge your end customer, not the dealer cost you pay to Keder.
                  This field is optional when creating orders. If not provided, no commission will be tracked for that order.
                </p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-4">
              {hasChanges && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-6 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 transition-colors"
                >
                  Reset Changes
                </button>
              )}
              <button
                type="submit"
                disabled={saving || !hasChanges}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
